/**
 * Published product reference data for the Pipe Sketch material database.
 *
 * Everything in this file is a *tabulated* figure taken from a published
 * standard, a manufacturer weight table or a market price list — nothing here
 * is a size curve. Keys are nominal pipe size (NPS) in inches.
 *
 * Sources
 *  • Butt-weld fitting weights — manufacturer weight tables published against
 *    ASME B16.9 (Hackney/Ladish series as reproduced by Wermac), STD & XS.
 *  • 45° LR elbow centre-to-end — ASME B16.9-2018 Table 3 (mm converted).
 *  • ASME B16.5 Class 150 weld-neck / blind flange weights — Weldbend
 *    calculated flange weight tables.
 *  • Valve prices — 2025 US distributor net pricing (bronze threaded through
 *    2", iron/steel Class 150 flanged 2-1/2" and up); butterfly from ductile
 *    iron wafer lever/gear list.
 *  • Pipe $/lb — back-figured from current distributor pricing
 *    (A106 Gr.B 4" Sch 40 seamless ≈ $26.57/ft ÷ 10.79 lb/ft ≈ $2.46/lb).
 *  • Insulation — ASTM C547 fibreglass pipe cover retail/contract pricing
 *    normalised to the covering annulus, jacket and coating from insulation
 *    contractor unit-price data.
 *
 * These are seed prices. Users override any of them on the device from the
 * Mat'l tab, so the tool always works offline with no live pricing feed.
 */

import type { ComponentKind, SupportKind } from "@/lib/pipe-spec";

type BySize = Record<number, number>;

/* ------------------------------------------------------------------ */
/* ASME B16.9 butt-weld fittings — weights, lb each                    */
/* ------------------------------------------------------------------ */

/** Long-radius 90° elbow, standard weight (Sch 40) / extra strong (Sch 80). */
export const BW_90_LB_STD: BySize = {
  0.5: 0.18, 0.75: 0.19, 1: 0.4, 1.25: 0.6, 1.5: 0.9, 2: 1.6, 2.5: 3.25,
  3: 5.0, 4: 9.0, 5: 15.0, 6: 24.5, 8: 50.0, 10: 88.0, 12: 125.0,
};
export const BW_90_LB_XS: BySize = {
  0.5: 0.25, 0.75: 0.25, 1: 0.5, 1.25: 0.9, 1.5: 1.15, 2: 2.2, 2.5: 4.0,
  3: 6.5, 4: 13.5, 5: 22.0, 6: 35.0, 8: 71.0, 10: 107.0, 12: 160.0,
};

/** Long-radius 45° elbow. */
export const BW_45_LB_STD: BySize = {
  0.5: 0.09, 0.75: 0.09, 1: 0.25, 1.25: 0.38, 1.5: 0.4, 2: 0.81, 2.5: 1.75,
  3: 2.63, 4: 4.5, 5: 7.5, 6: 12.0, 8: 23.0, 10: 43.0, 12: 62.0,
};

/** Straight tee. */
export const BW_TEE_LB_STD: BySize = {
  0.5: 0.35, 0.75: 0.45, 1: 0.75, 1.25: 1.3, 1.5: 2.0, 2: 3.5, 2.5: 6.0,
  3: 7.0, 4: 12.0, 5: 21.0, 6: 34.0, 8: 55.0, 10: 85.0, 12: 120.0,
};

/** Concentric reducer, one size down. */
export const BW_RED_LB_STD: BySize = {
  0.5: 0.17, 0.75: 0.4, 1: 0.5, 1.25: 0.7, 1.5: 0.9, 2: 1.5, 2.5: 2.0,
  3: 3.15, 4: 3.5, 5: 8.5, 6: 13.2, 8: 22.0, 10: 34.0, 12: 60.0,
};

/** XS-to-STD weight ratio for the fittings that only publish a STD column. */
export const XS_WEIGHT_RATIO = 1.35;

/** ASME B16.9 Table 3 — 45° LR elbow centre-to-end, inches. */
export const BW_45_C: BySize = {
  0.5: 0.63, 0.75: 0.75, 1: 0.88, 1.25: 1.0, 1.5: 1.12, 2: 1.38, 2.5: 1.75,
  3: 2.0, 4: 2.5, 5: 3.12, 6: 3.75, 8: 5.0, 10: 6.25, 12: 7.5,
};

/* ------------------------------------------------------------------ */
/* ASME B16.5 Class 150 flanges — weights, lb each                     */
/* ------------------------------------------------------------------ */

export const WN_FLANGE_LB: BySize = {
  0.5: 2, 0.75: 2, 1: 2, 1.25: 4, 1.5: 5, 2: 9, 2.5: 11, 3: 14,
  4: 16, 5: 21, 6: 25, 8: 40, 10: 56, 12: 86,
};

export const BLIND_FLANGE_LB: BySize = {
  0.5: 1, 0.75: 1, 1: 2, 1.25: 3, 1.5: 3, 2: 5, 2.5: 8, 3: 9,
  4: 12, 5: 15, 6: 19, 8: 29, 10: 44, 12: 70,
};

/* ------------------------------------------------------------------ */
/* Mill / shop material prices                                         */
/* ------------------------------------------------------------------ */

/** Pipe, $ per pound of plain-end pipe. */
export const PIPE_PRICE_PER_LB: Record<string, number> = {
  "cs-a106-sch40": 2.46,
  "cs-a106-sch80": 2.58,
  "ss-304-sch10s": 7.2,
  "ss-304-sch40s": 6.8,
  "cu-type-l": 11.5,
  "pvc-sch40": 4.0,
};

/** Standard stock length purchased, feet. */
export const STOCK_LENGTH_FT: Record<string, number> = {
  "cs-a106-sch40": 21,
  "cs-a106-sch80": 21,
  "ss-304-sch10s": 20,
  "ss-304-sch40s": 20,
  "cu-type-l": 20,
  "pvc-sch40": 20,
};

/** Butt-weld fitting price, $ per pound, plus a per-piece handling minimum. */
export const FITTING_PRICE_PER_LB: Record<string, number> = {
  "cs-a106-sch40": 4.2,
  "cs-a106-sch80": 4.6,
  "ss-304-sch10s": 15.5,
  "ss-304-sch40s": 15.5,
  "cu-type-l": 14.0,
  "pvc-sch40": 3.6,
};

export const FITTING_MIN_PRICE: Record<string, number> = {
  "cs-a106-sch40": 3.5,
  "cs-a106-sch80": 4.0,
  "ss-304-sch10s": 9.0,
  "ss-304-sch40s": 9.0,
  "cu-type-l": 2.2,
  "pvc-sch40": 1.1,
};

/** Flange / fabricated steel, $ per pound. */
export const FLANGE_PRICE_PER_LB = 3.1;

/** Density of the alloy relative to carbon steel, for fitting weights. */
export const DENSITY_FACTOR: Record<string, number> = {
  "cs-a106-sch40": 1,
  "cs-a106-sch80": 1,
  "ss-304-sch10s": 1.02,
  "ss-304-sch40s": 1.02,
  "cu-type-l": 1.14,
  "pvc-sch40": 0.18,
};

/* ------------------------------------------------------------------ */
/* Valves & specialties — $ each, 2025 distributor net                 */
/* ------------------------------------------------------------------ */

export const VALVE_PRICE: Partial<Record<ComponentKind, BySize>> = {
  gate: {
    0.5: 66, 0.75: 80, 1: 101, 1.25: 154, 1.5: 179, 2: 234, 2.5: 520,
    3: 610, 4: 840, 5: 1150, 6: 1380, 8: 2250, 10: 3600, 12: 5200,
  },
  ball: {
    0.5: 22, 0.75: 28, 1: 40, 1.25: 62, 1.5: 85, 2: 120, 2.5: 480,
    3: 560, 4: 830, 5: 1300, 6: 1650, 8: 3200, 10: 5200, 12: 7600,
  },
  globe: {
    0.5: 95, 0.75: 115, 1: 150, 1.25: 215, 1.5: 265, 2: 355, 2.5: 780,
    3: 940, 4: 1450, 5: 2100, 6: 2650, 8: 4300, 10: 6900, 12: 9800,
  },
  check: {
    0.5: 52, 0.75: 62, 1: 82, 1.25: 120, 1.5: 145, 2: 195, 2.5: 430,
    3: 505, 4: 690, 5: 980, 6: 1180, 8: 1950, 10: 3100, 12: 4500,
  },
  butterfly: {
    2: 79, 2.5: 95, 3: 108, 4: 150, 5: 185, 6: 221, 8: 337, 10: 480, 12: 722,
  },
  strainer: {
    0.5: 45, 0.75: 52, 1: 66, 1.25: 95, 1.5: 120, 2: 165, 2.5: 390,
    3: 460, 4: 660, 5: 950, 6: 1180, 8: 2100, 10: 3400, 12: 5100,
  },
  union: {
    0.5: 6, 0.75: 8, 1: 11, 1.25: 16, 1.5: 20, 2: 28, 2.5: 52, 3: 68,
    4: 110, 5: 170, 6: 230, 8: 420, 10: 700, 12: 1000,
  },
};

/** Valve body weights, lb — bronze threaded to 2", iron Class 150 above. */
export const VALVE_WEIGHT_LB: Partial<Record<ComponentKind, BySize>> = {
  gate: {
    0.5: 0.8, 0.75: 1.5, 1: 2.0, 1.25: 3.1, 1.5: 3.8, 2: 6.4, 2.5: 45,
    3: 55, 4: 84, 5: 120, 6: 160, 8: 290, 10: 450, 12: 650,
  },
  ball: {
    0.5: 0.6, 0.75: 1.0, 1: 1.6, 1.25: 2.6, 1.5: 3.4, 2: 5.5, 2.5: 34,
    3: 44, 4: 70, 5: 100, 6: 135, 8: 250, 10: 400, 12: 590,
  },
  globe: {
    0.5: 1.0, 0.75: 1.8, 1: 2.6, 1.25: 4.0, 1.5: 5.2, 2: 8.5, 2.5: 52,
    3: 66, 4: 105, 5: 150, 6: 200, 8: 360, 10: 570, 12: 830,
  },
  check: {
    0.5: 0.7, 0.75: 1.2, 1: 1.8, 1.25: 2.7, 1.5: 3.4, 2: 5.4, 2.5: 32,
    3: 40, 4: 60, 5: 88, 6: 115, 8: 205, 10: 330, 12: 480,
  },
  butterfly: {
    2: 8, 2.5: 10, 3: 12, 4: 16, 5: 21, 6: 27, 8: 45, 10: 68, 12: 100,
  },
  strainer: {
    0.5: 1.0, 0.75: 1.6, 1: 2.4, 1.25: 3.6, 1.5: 4.6, 2: 7.5, 2.5: 40,
    3: 50, 4: 78, 5: 110, 6: 150, 8: 270, 10: 420, 12: 620,
  },
  union: {
    0.5: 0.5, 0.75: 0.7, 1: 1.0, 1.25: 1.5, 1.5: 1.9, 2: 3.0, 2.5: 4.6,
    3: 6.0, 4: 9.5, 5: 14, 6: 19, 8: 32, 10: 50, 12: 74,
  },
};

/* ------------------------------------------------------------------ */
/* Supports — MSS SP-58 hardware, $ each                               */
/* ------------------------------------------------------------------ */

/** Fig. 65 clevis hanger with rod, nuts and insert — $ each. */
export const HANGER_PRICE: BySize = {
  0.5: 9, 0.75: 9, 1: 10, 1.25: 11, 1.5: 12, 2: 14, 2.5: 18, 3: 21,
  4: 28, 5: 38, 6: 46, 8: 72, 10: 105, 12: 145,
};

/** Cost of each support type relative to a clevis hanger of the same size. */
export const SUPPORT_PRICE_MULT: Record<SupportKind, number> = {
  hanger: 1,
  trapeze: 3.4,
  guide: 3.0,
  anchor: 2.6,
  spring: 9.0,
  shoe: 2.2,
  roller: 3.2,
  riserClamp: 1.6,
};

/* ------------------------------------------------------------------ */
/* Insulation, jacket & coating                                        */
/* ------------------------------------------------------------------ */

/**
 * Pipe covering, $ per square inch of covering annulus per foot, plus a
 * per-foot handling adder. Calibrated to ASTM C547 fibreglass pipe cover
 * (1-1/2" IPS × 1-1/2" wall ≈ $14/ft supplied).
 */
export const INSUL_PRICE_COEF: Record<string, number> = {
  none: 0,
  fiberglass: 0.62,
  calsil: 1.55,
  elastomeric: 0.95,
  mineralwool: 0.85,
};

export const INSUL_PRICE_ADDER = 1.2;

/** Jacket / lagging, $ per square foot supplied. */
export const JACKET_PRICE: Record<string, number> = {
  none: 0,
  asj: 0.95,
  aluminum: 3.6,
  stainless: 7.4,
  pvc: 2.6,
};

/** Coating, $ per gallon. */
export const COATING_PRICE: Record<string, number> = {
  none: 0,
  primer: 52,
  primerFinish: 62,
  hightemp: 135,
};

/** Nearest tabulated size lookup — sizes off the table interpolate on NPS. */
export function bySize(table: BySize | undefined, nom: number, fallback = 0): number {
  if (!table) return fallback;
  const exact = table[nom];
  if (typeof exact === "number") return exact;
  const keys = Object.keys(table).map(Number).sort((a, b) => a - b);
  if (!keys.length) return fallback;
  if (nom <= keys[0]) return table[keys[0]];
  if (nom >= keys[keys.length - 1]) {
    const last = keys[keys.length - 1];
    const prev = keys[keys.length - 2] ?? last;
    const slope = prev === last ? 0 : (table[last] - table[prev]) / (last - prev);
    return Math.max(0, table[last] + slope * (nom - last));
  }
  for (let i = 1; i < keys.length; i += 1) {
    if (nom < keys[i]) {
      const a = keys[i - 1];
      const b = keys[i];
      const t = (nom - a) / (b - a);
      return table[a] + (table[b] - table[a]) * t;
    }
  }
  return fallback;
}
